export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startTime: number = 0;
  private pausedDuration: number = 0;
  private pauseStartTime: number = 0;
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array | null = null;

  /**
   * Initialize microphone for testing (without starting recording)
   */
  async initializeMicrophone(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 48000,
        },
      });

      // Set up audio analysis for volume visualization
      this.setupAudioAnalysis();
    } catch (error) {
      console.error('Failed to initialize microphone:', error);
      throw new Error('Failed to access microphone. Please check permissions.');
    }
  }

  /**
   * Request microphone permission and start recording
   */
  async start(): Promise<void> {
    try {
      // If mic not initialized, do it now
      if (!this.stream) {
        await this.initializeMicrophone();
      }

      // Prefer Opus codec for best voice compression
      const mimeType = this.getSupportedMimeType();

      if (!this.stream) {
        throw new Error('No media stream available');
      }

      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
      this.chunks = [];
      this.startTime = Date.now();
      this.pausedDuration = 0;

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.chunks.push(event.data);
        }
      };

      // Collect chunks every 1 second
      this.mediaRecorder.start(1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      throw new Error('Failed to access microphone. Please check permissions.');
    }
  }

  /**
   * Stop recording and return the audio blob
   */
  stop(): Promise<{ blob: Blob; duration: number }> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('Recorder not started'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.chunks[0]?.type || 'audio/webm' });
        const duration = Math.floor((Date.now() - this.startTime) / 1000);

        // Stop all tracks to release microphone
        this.stream?.getTracks().forEach((track) => track.stop());

        resolve({ blob, duration });
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        reject(new Error('Recording failed'));
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Pause recording
   */
  pause(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this.pauseStartTime = Date.now();
    }
  }

  /**
   * Resume recording
   */
  resume(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.pausedDuration += Date.now() - this.pauseStartTime;
      this.mediaRecorder.resume();
    }
  }

  /**
   * Cancel recording and discard all data
   */
  cancel(): void {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.chunks = [];
    }
    this.stream?.getTracks().forEach((track) => track.stop());
    this.audioContext?.close();
    this.mediaRecorder = null;
    this.stream = null;
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
  }

  /**
   * Get current recording time in seconds (excluding paused time)
   */
  getRecordingTime(): number {
    if (!this.mediaRecorder) {
      return 0;
    }
    const elapsed = Date.now() - this.startTime;
    const pausedTime = this.mediaRecorder.state === 'paused'
      ? (Date.now() - this.pauseStartTime)
      : 0;
    return Math.floor((elapsed - this.pausedDuration - pausedTime) / 1000);
  }

  /**
   * Check if currently recording
   */
  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  /**
   * Check if currently paused
   */
  isPaused(): boolean {
    return this.mediaRecorder?.state === 'paused';
  }

  /**
   * Get current audio volume level (0-100)
   */
  getVolumeLevel(): number {
    if (!this.analyser || !this.dataArray) {
      return 0;
    }

    // @ts-expect-error - ArrayBufferLike is compatible with Uint8Array at runtime
    this.analyser.getByteFrequencyData(this.dataArray);

    // Calculate average volume
    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      sum += this.dataArray[i];
    }
    const average = sum / this.dataArray.length;

    // Normalize to 0-100 range
    return Math.min(100, (average / 255) * 100);
  }

  /**
   * Get audio context for visualization
   */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * Get analyser node for visualization
   */
  getAnalyser(): AnalyserNode | null {
    return this.analyser;
  }

  /**
   * Set up audio analysis for volume visualization
   */
  private setupAudioAnalysis(): void {
    if (!this.stream) return;

    try {
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;

      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      const source = this.audioContext.createMediaStreamSource(this.stream);
      source.connect(this.analyser);
    } catch (error) {
      console.error('Failed to setup audio analysis:', error);
    }
  }

  /**
   * Get supported MIME type for recording
   */
  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return ''; // Will use browser default
  }

  /**
   * Check if the browser supports audio recording
   */
  static isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof window.MediaRecorder === 'function'
    );
  }
}
